"use strict";

import NodeBase from "../util/NodeBase";
import { drawRoundRect } from "../util/shapes";

/**
 * A Notation Node/Cluster shape.
 *
 * @augments NodeBase
 */
class Notation extends NodeBase {
  /**
   * @param {object} options
   * @param {object} body
   * @param {Label} labelModule
   */
  constructor(options, body, labelModule, captionModule, notationModule, iconsList) {
    super(options, body, labelModule, captionModule, notationModule);
    this._setMargins(labelModule);

    this.iconsList = iconsList;
    this.corner = { w: 6, h: 6 }
    this.icon = { width: 14, height: 14, margin: 2};
    this.mode = "move";

    this.toolbar = [
      { item: "lock", name: ["lock", "lock", "lock", "lock"], show: true, type: options.toolbar.lock,  left: 0, top: 0, right: 0, bottom: 0 },
      { item: "pin", name: ["pin", "pin", "pin", "pin"], show: true, type: options.toolbar.pin,  left: 0, top: 0, right: 0, bottom: 0 },
    ]

    if (options.fixToolBar === false) {
      this.toolbar = [
        { item: "lock", name: ["lock", "lock", "lock", "lock"], show: (options.toolbar.lock === -1) ? false : true, type: options.toolbar.lock, left: 0, top: 0, right: 0, bottom: 0 },
        { item: "pin", name: ["pin", "pin", "pin", "pin"], show: (options.toolbar.pin === -1) ? false : true, type: options.toolbar.pin, left: 0, top: 0, right: 0, bottom: 0 },
      ]
    }

    const offset = 2;
    for (let i = 0, idx = 0, len = this.toolbar.length; i < len; i++) {
      if (!this.toolbar[i].show) {
        continue;
      }
      // offset
      this.toolbar[i].left = (this.icon.width + this.icon.margin + this.icon.margin) * idx + offset;
      this.toolbar[i].right = this.toolbar[i].left + (this.icon.width + this.icon.margin + this.icon.margin);
      this.toolbar[i].top = 1;
      this.toolbar[i].bottom = this.icon.height + 1;
      idx++;
    }
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} [selected]
   * @param {boolean} [hover]
   */
  resize(ctx, selected = this.selected, hover = this.hover) {
    if (this.needsRefresh(selected, hover)) {
      const dimensions = this.getDimensionsFromLabel(ctx, selected, hover);
      const width = dimensions.width + this.margin.right + this.margin.left;
      const height = dimensions.height + this.margin.top + this.margin.bottom;
      if (this.width != width || this.height != height) {
        this.width = width;
        this.height = height;
        this.radius = this.width / 2;
        this.updateToolbar(this.toolbar);

        this.boundingBox.left = this.left;
        this.boundingBox.top = this.top;
        this.boundingBox.bottom = this.top + this.height;
        this.boundingBox.right = this.left + this.width;

        if (this.top != undefined && this.left != undefined) {
          this.body.emitter.emit('changeLayout', { nodes: [this.options.id], edges: [], x: this.left, y: this.top, width: width, height: height});
        }
      }
    }
  }

  /**
   *
   * @param {number} x width
   * @param {number} y height
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
  updateBoundingBox(x, y, ctx, selected, hover) {
    this.left = x ;
    this.top = y;

    const borderRadius = this.options.shapeProperties.borderRadius; // only effective for box
    this._addBoundingBoxMargin(borderRadius);
    if (ctx !== undefined) {
      this.resize(ctx, selected, hover);
    }
    this.boundingBox.left = this.left;
    this.boundingBox.top = this.top;
    this.boundingBox.bottom = this.top + this.height;
    this.boundingBox.right = this.left + this.width;
  }

  /**
   * @param {object}} [toolbar]
   */
  updateToolbar(toolbar) {
    for (let i = 0, len = this.toolbar.length; i < len; i++) {
      if (toolbar[this.toolbar[i].item] != undefined) {
        this.toolbar[i].type = toolbar[this.toolbar[i].item]
      }
      if (this.options.fixToolBar === false) {
        this.toolbar[i].show = (this.toolbar[i].type === -1 ? false : true);
      }
    }
    if (this.options.fixToolBar === false) {
      const right = this.width - (this.icon.width + this.icon.margin) * 2;
      for (let i = 0, idx = 0, len = this.toolbar.length; i < len; i++) {
        if (!this.toolbar[i].show) {
          this.toolbar[i].left = 0;
          this.toolbar[i].right = 0;
          this.toolbar[i].top = 0;
          this.toolbar[i].bottom = 0;
          continue;
        }
        idx++;
        this.toolbar[i].left = right - (this.icon.width + this.icon.margin) * idx;
        this.toolbar[i].right = this.toolbar[i].left + (this.icon.width);
        this.toolbar[i].top = 1;
        this.toolbar[i].bottom = this.icon.height + 1;
      }
    }

    return true;
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x width
   * @param {number} y height
   * @param {boolean} selected
   * @param {boolean} hover
   * @param {ArrowOptions} values
   */
  draw(ctx, x, y, selected, hover, values, pointer) {
    this.left = x;// - this.width / 2;
    this.top = y;// - this.height / 2;
    this.mode = values.mode;

    this.resize(ctx, selected, hover);
    this.initContextForDraw(ctx, values);
    drawRoundRect(ctx, this.left, this.top, this.width, this.height, values.borderRadius);
    this.performFill(ctx, values);

    this.drawSelected(ctx, selected, this.mode);
    this.updateBoundingBox(x, y, ctx, selected, hover);
    this.drawLabel(ctx, selected, hover);
    this.drawStatusBar(ctx, pointer, selected, hover);
    this.hitCorner(selected, hover, pointer, this.mode);
    if (values.useToolBar) {
      this.drawToolBar(ctx, pointer, selected, hover);
    }
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} mode
   */
  drawSelected(ctx, selected, mode) {
    if (selected) { // if (selected && mode == "move") {
      if (this.options.useToolBar && this.options.toolbar && this.options.toolbar.pin == 3) {
        return;
      }
      ctx.save();
      ctx.fillStyle = '#C95B55';
      ctx.fillRect(this.left + this.width - this.corner.w, this.top + this.height - this.corner.h, this.corner.w, this.corner.h);
      ctx.fillRect(this.left, this.top, this.corner.w, this.corner.h);

      // ctx.fillRect(this.left, this.top + this.height - this.corner.h, this.corner.w, this.corner.h);
      // ctx.fillRect(this.left + this.width - this.corner.w, this.top, this.corner.w, this.corner.h);
      ctx.restore();
    }
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param pointer
   * @param {boolean} selected
   * @param {boolean} hover
   */
  drawStatusBar(ctx, pointer, selected, hover) {
    if (!selected) {
      return ;
    }
    let close_state = "normal";
    let edit_state = "normal";
    let anchor_state = "normal";
    if (hover) {
      if (pointer.y >= (this.top + 4) && pointer.y <= (this.top + 4 + this.icon.height + 4) &&
        pointer.x >= (this.left + this.width - this.icon.width - 2 - this.icon.width - 2 - this.icon.width - 2 - 2) &&
        pointer.x <= (this.left + this.width - this.icon.width - 2 - this.icon.width - 2 - 2)) {
          anchor_state = "hover";
      } else if (pointer.y >= (this.top + 4) && pointer.y <= (this.top + 4 + this.icon.height + 4) &&
        pointer.x >= (this.left + this.width - this.icon.width - 2 - this.icon.width - 2 - 2) &&
        pointer.x <= (this.left + this.width - this.icon.width - 2 - 2)) {
          edit_state = "hover";
      } else if (pointer.y >= (this.top + 4) && pointer.y <= (this.top + 4 + this.icon.height + 4) &&
        pointer.x >= (this.left + this.width - this.icon.width - 2 - 2) &&
        pointer.x <= (this.left + this.width - 2)) {
          close_state = "hover";
      }
    }
    ctx.drawImage(this.iconsList.close[close_state], this.left + this.width - this.icon.width - 2, this.top + 1, this.icon.width, this.icon.height);
    ctx.drawImage(this.iconsList.edit[edit_state], this.left + this.width - this.icon.width - 2 - this.icon.width - 2, this.top + 1, this.icon.width, this.icon.height);
    // ctx.drawImage(this.iconsList.anchor[anchor_state], this.left + this.width - this.icon.width - 2 - this.icon.width - 2 - this.icon.width - 2, this.top + 4, this.icon.width, this.icon.height);
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param pointer
   * @param {boolean} selected
   * @param {boolean} hover
   */
  drawToolBar(ctx, pointer, selected, hover) {
    if (!selected) {
      return;
    }
    for (let i = 0, state = "normal", len = this.toolbar.length; i < len; i++, state = "normal") {
      const img = this.toolbar[i].item + this.toolbar[i].type;
      if (!this.toolbar[i].show || !(this.iconsList[img])) {
        continue;
      }
      if (hover && this.toolbar[i].type > 1) {
        if (pointer.x >= this.left + this.toolbar[i].left && pointer.x <= this.left + this.toolbar[i].right &&
            pointer.y >= this.top + this.toolbar[i].top && pointer.y <= this.top + this.toolbar[i].bottom)
        {
          state = "hover";
        }
      }

      ctx.drawImage(this.iconsList[img][state],
                    this.left + this.toolbar[i].left,
                    this.top + this.toolbar[i].top,
                    this.icon.width,
                    this.icon.height);
    }
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} angle
   * @returns {number}
   */
  distanceToBorder(ctx, angle) {
    if (ctx) {
      this.resize(ctx);
    }
    const borderWidth = this.options.borderWidth;

    return (
      Math.min(
        Math.abs(this.width / 2 / Math.cos(angle)),
        Math.abs(this.height / 2 / Math.sin(angle))
      ) + borderWidth
    );
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
   drawLabel(ctx, selected, hover) {
    let ls = this.left + this.margin.left;
    let ts = this.top + this.margin.top; // (this.top + this.height / 2 + 4); //this.top + capH + 8 - this.textSize.height / 2;
    this.labelModule.draw(ctx, ls, ts, selected, hover, "top", "left");
    // this.labelModule.draw(ctx, ls, ts, selected, hover, "middle", "left");
  }

  /**
   * @param {x: number, y: number} pointer
   */
  hitCorner(selected, hover, pointer, mode) {
    if (!selected || !hover) {
      return;
    }
    if (this.options.useToolBar && this.options.toolbar && this.options.toolbar.pin == 3) {
      return;
    }
    if (pointer.x >= this.left + this.width - this.corner.w && pointer.x <= this.left + this.width && pointer.y >= this.top + this.height - this.corner.h && pointer.y <= this.top + this.height) {
      if (this.body.container) {
        this.body.container.style.cursor = "se-resize";
      }
    } else if (pointer.x >= this.left && pointer.x <= this.left + this.corner.w && pointer.y >= this.top && pointer.y <= this.top + this.corner.h) {
      if (this.body.container) {
        this.body.container.style.cursor = "se-resize";
      }
    // } else if (pointer.x >= this.left + this.width - this.corner.w && pointer.x <= this.left + this.width && pointer.y >= this.top && pointer.y <= this.top + this.corner.h) {
    //   if (this.body.container) {
    //     this.body.container.style.cursor = "sw-resize";
    //   }
    // } else if (pointer.x >= this.left && pointer.x <= this.left + this.corner.w && pointer.y >= this.top + this.height - this.corner.h && pointer.y <= this.top + this.height) {
    //   if (this.body.container) {
    //     this.body.container.style.cursor = "sw-resize";
    //   }
    } else if (selected && hover) {
      if (this.body.container) {
        this.body.container.style.cursor = "auto";
      }
    }
  }

  /**
   * @param {x: number, y: number} pointer
   */
  hitCorner_ori(selected, hover, pointer, mode) {
    if (mode != "move") {
      if (this.body.container) {
        this.body.container.style.cursor = "auto"; //"crosshair";
      }
    } else {
      if (selected && hover && pointer.x >= this.left + this.width - this.corner.w && pointer.x <= this.left + this.width && pointer.y >= this.top + this.height - this.corner.h && pointer.y <= this.top + this.height) {
        if (this.body.container) {
          this.body.container.style.cursor = "se-resize";
        }
      } else {
        if (this.body.container) {

          this.body.container.style.cursor = "auto";
        }
      }
    }
  }

  /**
   * @param {x: number, y: number} pointer
   */
  hitActionItem(pointer) {
    let item = "";
    if (/*this.mode === "move" && */pointer.x >= this.left && pointer.x <= this.left + this.corner.w &&
      pointer.y >= this.top && pointer.y <= this.top + this.corner.h) {
      item = "se-resize1";
    }
    if (/*this.mode === "move" && */pointer.x >= this.left + this.width - this.corner.w && pointer.x <= this.left + this.width &&
      pointer.y >= this.top + this.height - this.corner.h && pointer.y <= this.top + this.height) {
      item = "se-resize2";
    }

    if (this.options.useToolBar && this.options.toolbar && this.options.toolbar.pin == 3 && item != "") {
      return "";
    }
    if (item != "") {
      return item;
    }
    // if (/*this.mode === "move" && */pointer.x >= this.left + this.width - this.corner.w && pointer.x <= this.left + this.width &&
    //   pointer.y >= this.top && pointer.y <= this.top + this.corner.h) {
    //   return "sw-resize1";
    // }
    // if (/*this.mode === "move" && */pointer.x >= this.left && pointer.x <= this.left + this.corner.w &&
    //   pointer.y >= this.top + this.height - this.corner.h && pointer.y <= this.top + this.height) {
    //   return "sw-resize2";
    // }

    for (let i = 0, len = this.toolbar.length; i < len; i++) {
      if (!this.toolbar[i].show) {
        continue;
      }
      if (pointer.x >= this.left + this.toolbar[i].left && pointer.x <= this.left + this.toolbar[i].right &&
        pointer.y >= this.top + this.toolbar[i].top && pointer.y <= this.top + this.toolbar[i].bottom) {
        return this.toolbar[i].item;
      }
    }

    if (pointer.y >= (this.top + 2) && pointer.y <= (this.top + 4 + this.icon.height + 2)) {
      if (pointer.x >= (this.left + this.width - this.icon.width - 2 - this.icon.width - 2 - 2) &&
        pointer.x <= (this.left + this.width - this.icon.width - 2 - 2)) {
        return "edit";
      } else if (pointer.x >= (this.left + this.width - this.icon.width - 2 - 2) &&
        pointer.x <= (this.left + this.width - 2)) {
        return "close";
      }
    }
    return "";
  }

  /**
   * @param {x: number, y: number} pointer
   */
  overActionItemName(pointer) {
    for (let i = 0, len = this.toolbar.length; i < len; i++) {
      if (!this.toolbar[i].show) {
        continue;
      }
      if (pointer.x >= this.left + this.toolbar[i].left && pointer.x <= this.left + this.toolbar[i].right &&
        pointer.y >= this.top + this.toolbar[i].top && pointer.y <= this.top + this.toolbar[i].bottom) {
        return this.toolbar[i].name[this.options.toolbar[this.toolbar[i].item]];
      }
    }
    if (pointer.y >= (this.top + 2) && pointer.y <= (this.top + 4 + this.icon.height + 2)) {
      if (pointer.x >= (this.left + this.width - this.icon.width - 2 - this.icon.width - 2 - 2) &&
        pointer.x <= (this.left + this.width - this.icon.width - 2 - 2)) {
        return "edit";
      } else if (pointer.x >= (this.left + this.width - this.icon.width - 2 - 2) &&
        pointer.x <= (this.left + this.width - 2)) {
        return "close";
      }
    }
  }
}

export default Notation;
